import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './authConfig';

const msalInstance = new PublicClientApplication(msalConfig);

const root = ReactDOM.createRoot(document.getElementById('root'));

// ── Last-resort safety net ──
// Whatever breaks (a MSAL glitch, a genuine bug, corrupted cached sign-in
// data) NOTHING should ever leave someone staring at a blank white page
// with no way forward. This catches any render-time crash anywhere in the
// app and offers two one-click recovery options instead.
class RootErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Fatal app error:", error, info);
  }
  clearSessionAndReload = () => {
    try {
      // Wipe MSAL's cached sign-in state specifically (not other sites'
      // data) — this is the fix for the class of bug where a stale/corrupt
      // cached account or token makes the app crash on every single load.
      Object.keys(localStorage)
        .filter(k => k.toLowerCase().includes("msal"))
        .forEach(k => localStorage.removeItem(k));
      sessionStorage.clear();
    } catch (e) { /* ignore — storage may be unavailable, still reload below */ }
    window.location.reload();
  };
  render() {
    if (this.state.error) {
      return (
        <div style={{
          minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
          background: "#0f172a", fontFamily: "'Inter',system-ui,-apple-system,sans-serif", padding: 20
        }}>
          <div style={{
            width: "100%", maxWidth: 420, background: "#1e293b", border: "1px solid #334155",
            borderRadius: 12, padding: "32px 28px", textAlign: "center", color: "#e2e8f0"
          }}>
            <div style={{ fontSize: 30, marginBottom: 10 }}>⚠️</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Something went wrong</div>
            <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 22, lineHeight: 1.5 }}>
              The dashboard hit an unexpected error while loading. This is usually fixed by reloading —
              if that doesn't work, resetting your saved sign-in below will.
            </div>
            <button onClick={() => window.location.reload()} style={{
              width: "100%", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8,
              padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 10
            }}>Reload the page</button>
            <button onClick={this.clearSessionAndReload} style={{
              width: "100%", background: "transparent", color: "#94a3b8", border: "1px solid #334155",
              borderRadius: 8, padding: "10px", fontSize: 12.5, fontWeight: 700, cursor: "pointer"
            }}>Reset sign-in and reload</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function renderApp() {
  root.render(
    <React.StrictMode>
      <RootErrorBoundary>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </RootErrorBoundary>
    </React.StrictMode>
  );
}

// PublicClientApplication.initialize() must resolve before ANY other MSAL
// method is called — getAllAccounts(), setActiveAccount(), addEventCallback()
// included. Calling those earlier throws "uninitialized_public_client_
// application" synchronously, which — because it happened at the top of this
// file, before React ever mounted anything — used to blank the whole page
// with no recovery screen possible, every single time a cached sign-in
// existed (i.e. every load after the very first one). Everything MSAL-related
// now happens inside this .then(), after initialize() has actually finished.
msalInstance.initialize().then(() => {
  // If a previous sign-in already picked an account (e.g. after a page
  // refresh), make it the active one so useMsal()/useIsAuthenticated() are
  // correct on first render instead of briefly reporting "signed out".
  if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }
  msalInstance.addEventCallback(event => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });
  renderApp();
}).catch(err => {
  // eslint-disable-next-line no-console
  console.error("MSAL failed to initialize:", err);
  root.render(<RootErrorBoundary><ThrowOnMount error={err} /></RootErrorBoundary>);
});

// Small helper so the initialize()-rejection path reuses the exact same
// recovery UI as any other crash, instead of a second copy of it.
function ThrowOnMount({ error }) {
  throw error instanceof Error ? error : new Error(String(error));
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
